import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { PrimaryButton } from "@/components/PrimaryButton";
import { useApp, type LiveListing, type Product } from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";
import { findProductMatches } from "@/features/listingImportMatch";

export const Route = createFileRoute("/tools/listing-import")({
  component: ListingImportPage,
});

/** Shape returned by GET /api/ebay/listings/active. Mirrors
 *  ActiveEbayListing in apps/api/src/lib/ebayClient.ts. */
interface RemoteListing {
  itemId: string;
  sku: string;
  title: string;
  listingType: "FixedPrice" | "Auction";
  price?: number;
  buyItNowPrice?: number;
  quantity?: number;
  viewItemUrl?: string;
}

/** Per-row decision in the wizard. `unselected` = operator hasn't chosen
 *  yet; `attach` = wire to an existing product; `create` = mint a new
 *  product on import; `skip` = leave alone. */
type RowDecision =
  | { kind: "unselected" }
  | { kind: "attach"; productId: string }
  | { kind: "create" }
  | { kind: "skip" };

interface RowState {
  decision: RowDecision;
  status: "idle" | "submitting" | "done" | "error";
  message?: string;
}

function ListingImportPage() {
  const { products, liveListings, mergeImportedListing } = useApp();
  const [remote, setRemote] = useState<RemoteListing[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [search, setSearch] = useState("");

  // Listings the local catalog already has — operator should see those
  // grayed out so they don't import duplicates. Match by ebayListingId.
  const importedItemIds = useMemo(
    () =>
      new Set(
        liveListings
          .map((l) => l.ebayListingId)
          .filter((id): id is string => !!id),
      ),
    [liveListings],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiFetch("/api/ebay/listings/active");
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setLoadError(
          body.error ??
            "eBay account not connected. Connect via Tools → eBay Integration first.",
        );
        return;
      }
      if (!res.ok) {
        setLoadError(`Failed to load active listings (${res.status}).`);
        return;
      }
      const data = (await res.json()) as RemoteListing[];
      setRemote(data);
      // Reset row decisions when a new fetch lands.
      const fresh: Record<string, RowState> = {};
      for (const r of data) {
        fresh[r.itemId] = { decision: { kind: "unselected" }, status: "idle" };
      }
      setRows(fresh);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setRow = (itemId: string, patch: Partial<RowState>) => {
    setRows((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...patch },
    }));
  };

  const filtered = useMemo(() => {
    if (!remote) return null;
    const q = search.trim().toLowerCase();
    return q
      ? remote.filter(
          (r) =>
            r.title.toLowerCase().includes(q) ||
            r.itemId.toLowerCase().includes(q) ||
            r.sku.toLowerCase().includes(q),
        )
      : remote;
  }, [remote, search]);

  const importRow = useCallback(
    async (row: RemoteListing) => {
      const state = rows[row.itemId];
      if (!state || state.decision.kind === "unselected") return;
      if (state.decision.kind === "skip") {
        setRow(row.itemId, { status: "done", message: "Skipped." });
        return;
      }
      setRow(row.itemId, { status: "submitting", message: undefined });
      try {
        const price = row.buyItNowPrice ?? row.price ?? 0;
        const body = {
          ebayItemId: row.itemId,
          title: row.title,
          price,
          quantity: row.quantity ?? 1,
          listingType: row.listingType,
          ebayListingUrl: row.viewItemUrl,
          ...(state.decision.kind === "attach"
            ? { productId: state.decision.productId }
            : {}),
        };
        const res = await apiFetch("/api/listings/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          setRow(row.itemId, {
            status: "error",
            message: errBody.error ?? `Import failed (${res.status}).`,
          });
          return;
        }
        const result = (await res.json()) as {
          createdProduct: boolean;
          productId: string;
          product: Product;
          listing: LiveListing;
        };
        // Seed both rows into local state so they appear in /products and
        // /live without a hard reload.
        mergeImportedListing(result.product, result.listing);
        setRow(row.itemId, {
          status: "done",
          message: result.createdProduct
            ? `Imported as new product (${result.productId.slice(0, 12)}…).`
            : `Attached to ${result.productId.slice(0, 12)}….`,
        });
      } catch (err) {
        setRow(row.itemId, {
          status: "error",
          message: err instanceof Error ? err.message : "Network error.",
        });
      }
    },
    [rows, mergeImportedListing],
  );

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <BackButton fallback={{ to: "/tools" }} variant="wide" />
        <div className="mt-2">
          <h1 className="text-2xl font-bold text-foreground">
            Import eBay Listings
          </h1>
          <p className="text-sm text-muted-foreground">
            Reconcile your active eBay listings into this catalog. Attach to an
            existing product, or create a fresh one from the listing's title and
            price.
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4">
        <div className="flex items-center gap-2">
          <PrimaryButton
            label={loading ? "Loading…" : "Reload active listings"}
            leadingIcon={
              loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )
            }
            onClick={() => void load()}
            disabled={loading}
            variant="secondary"
          />
          {remote && (
            <div className="text-xs text-muted-foreground">
              {remote.length} active on eBay · {importedItemIds.size} already
              imported
            </div>
          )}
        </div>

        {loadError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>{loadError}</div>
          </div>
        )}

        {remote && remote.length > 0 && (
          <label className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, item id, or SKU…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </label>
        )}

        {remote && filtered && filtered.length === 0 && (
          <div className="rounded-md border border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
            {search
              ? "No listings match your search."
              : "No active eBay listings found."}
          </div>
        )}

        {filtered &&
          filtered.map((row) => (
            <ImportRow
              key={row.itemId}
              row={row}
              products={products}
              state={
                rows[row.itemId] ?? {
                  decision: { kind: "unselected" },
                  status: "idle",
                }
              }
              alreadyImported={importedItemIds.has(row.itemId)}
              onChangeDecision={(decision) =>
                setRow(row.itemId, { decision, status: "idle" })
              }
              onImport={() => void importRow(row)}
            />
          ))}
      </div>
    </section>
  );
}

function ImportRow({
  row,
  products,
  state,
  alreadyImported,
  onChangeDecision,
  onImport,
}: {
  row: RemoteListing;
  products: Product[];
  state: RowState;
  alreadyImported: boolean;
  onChangeDecision: (decision: RowDecision) => void;
  onImport: () => void;
}) {
  const matches = useMemo(
    () =>
      findProductMatches({ sku: row.sku, title: row.title }, products).slice(
        0,
        5,
      ),
    [row.sku, row.title, products],
  );

  const decisionKind = state.decision.kind;
  const showProductPicker = decisionKind === "attach";
  const selectedProductId =
    state.decision.kind === "attach" ? state.decision.productId : "";

  return (
    <div
      className={`rounded-md border bg-card p-3 ${
        alreadyImported || state.status === "done"
          ? "border-border opacity-60"
          : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground line-clamp-2">
            {row.title}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span>Item {row.itemId}</span>
            {row.sku && <span>SKU {row.sku}</span>}
            <span>
              ${(row.buyItNowPrice ?? row.price ?? 0).toFixed(2)} ·{" "}
              {row.listingType}
            </span>
            <span>Qty {row.quantity ?? 1}</span>
            {row.viewItemUrl && (
              <a
                href={row.viewItemUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                eBay
              </a>
            )}
          </div>
        </div>
        {alreadyImported && (
          <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            already imported
          </span>
        )}
      </div>

      {!alreadyImported && state.status !== "done" && (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap gap-1.5">
            <DecisionPill
              label="Attach to product"
              active={decisionKind === "attach"}
              onClick={() =>
                onChangeDecision({
                  kind: "attach",
                  productId: matches[0]?.productId ?? "",
                })
              }
            />
            <DecisionPill
              label="Import as new"
              active={decisionKind === "create"}
              onClick={() => onChangeDecision({ kind: "create" })}
            />
            <DecisionPill
              label="Skip"
              active={decisionKind === "skip"}
              onClick={() => onChangeDecision({ kind: "skip" })}
            />
          </div>

          {showProductPicker && (
            <div className="space-y-1">
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                Target product
              </label>
              <select
                value={selectedProductId}
                onChange={(e) =>
                  onChangeDecision({
                    kind: "attach",
                    productId: e.target.value,
                  })
                }
                className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">Pick a product…</option>
                {matches.length > 0 && (
                  <optgroup label="Best matches">
                    {matches.map((m) => (
                      <option key={m.productId} value={m.productId}>
                        {m.name} {m.score > 0 ? `· ${m.score}%` : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="All products">
                  {products
                    .filter((p) => !p.archivedAt)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.listingTitle || p.id}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              disabled={
                state.status === "submitting" ||
                decisionKind === "unselected" ||
                (decisionKind === "attach" && !selectedProductId)
              }
              onClick={onImport}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {state.status === "submitting" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              {decisionKind === "skip" ? "Mark skipped" : "Import"}
            </button>
          </div>
        </div>
      )}

      {state.message && (
        <div
          className={`mt-2 flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] ${
            state.status === "error"
              ? "bg-destructive/10 text-destructive"
              : "bg-emerald-500/15 text-emerald-700"
          }`}
        >
          {state.status === "error" ? (
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          ) : (
            <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" />
          )}
          <span>{state.message}</span>
        </div>
      )}
    </div>
  );
}

function DecisionPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:bg-muted"
      }`}
    >
      {label}
    </button>
  );
}
